import { useState, useEffect } from 'react';
import { useAuthStore } from '@/shared/stores/authStore';
import {
  User,
  Calendar,
  MapPin,
  CreditCard,
  Shield,
  Check,
  AlertCircle,
  Building2,
  Wallet,
  FileText,
  Phone,
  Mail,
  Edit,
  Settings as SettingsIcon,
  Copy,
  CheckCheck
} from 'lucide-react';
import { Link } from 'react-router';
import Header from '@/react-app/components/Header';
import { api } from '@/shared/api/endpoints';
import { useTheme } from '@/react-app/contexts/ThemeContext';
import { useNavigate } from "react-router-dom";
import { mapToProfileData } from "@/shared/utils/transformers";
import type { ProfileData } from '@/shared/types';
import usdt from "../../assets/usdt.png";




export default function Profile() {
  const { user: authStoreUser } = useAuthStore();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [copiedStates, setCopiedStates] = useState<{ [key: string]: boolean }>({});

  const [profileData, setProfileData] = useState<ProfileData>({
    _id: '',
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    dob: '',
    address: '',
    profilePictureUrl: '',
    bankName: '',
    bankAccountNumber: '',
    bankAccountName: '',
    wallet_address: '',
    network: 'Binance Smart Chain',
    kycDocumentType: 'passport',
    kycDocumentUrl: '',
    kycVerificationStatus: 'pending',
    kycSubmittedAt: '',
    kycVerifiedAt: '',
    payMethod: '',
    token: '',
    updatePasswordDate: '',
    createdAt: ''
  });

  const tabs = [
    {
      id: 0,
      label: 'Personal Information',
      icon: User,
      description: 'View your personal details and profile information'
    },
    {
      id: 1,
      label: 'Payment Details',
      icon: CreditCard,
      description: 'View your payment and withdrawal information'
    },
    {
      id: 2,
      label: 'Security & Verification',
      icon: Shield,
      description: 'View your account security and verification status'
    }
  ];

  const documentTypes = {
    'INTL_PASSPORT': 'International Passport',
    'VOTERS_CARD': 'Voters Card',
    'NIN': 'National Identification Number (NIN)'
  };

  const copyToClipboard = async (text: string, field: string) => {
    if (!text || text === 'Not provided') return;

    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [field]: true }));

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [field]: false }));
      }, 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedStates(prev => ({ ...prev, [field]: true }));
        setTimeout(() => {
          setCopiedStates(prev => ({ ...prev, [field]: false }));
        }, 2000);
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
      }
      document.body.removeChild(textArea);
    }
  };

  useEffect(() => {
    if (authStoreUser) {
      fetchProfileData();
    }
  }, [authStoreUser]);

  const fetchProfileData = async () => {
    try {
      const response = await api.user.getProfile();
      if (response.success && response.data) {
        setProfileData(mapToProfileData(response.data));
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  // const getVerificationStatusBadge = (status: string) => {
  //   const statusConfig = {
  //     Not_Submitted: { color: 'red', text: 'Not_Submitted', icon: AlertCircle },
  //     Pending_Review: { color: 'yellow', text: 'Pending_Review', icon: AlertCircle },
  //     Verified: { color: 'green', text: 'Verified', icon: Check },
  //     Rejected: { color: 'red', text: 'Rejected', icon: AlertCircle },
  //   };

  //   const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.Pending_Review;
  //   const IconComponent = config.icon;

  //   return (
  //     <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color === 'green'
  //       ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
  //       : config.color === 'yellow'
  //         ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
  //         : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
  //       }`}>
  //       <IconComponent className="h-3 w-3 mr-1" />
  //       {config.text}
  //     </div>
  //   );
  // };


  const getVerificationStatusBadge = (status: string) => {
    let config: {
      color: "red" | "yellow" | "green";
      text: string;
      icon: any;
    };

    if (status === "Verified") {
      config = {
        color: "green",
        text: "Fully Verified – Full Access",
        icon: Check,
      };
    }
    // Unverified states
    else if (
      status === "Not_Submitted" ||
      status === "Pending_Review" ||
      status === "Rejected"
    ) {
      config = {
        color: "red",
        text: "Unverified – Cannot Invest",
        icon: AlertCircle,
      };
    }
    // Future / Partial verification
    else {
      config = {
        color: "red",
        text: "Not Submitted – Cannot Invest",
        icon: AlertCircle,
      };
    }

    const IconComponent = config.icon;

    return (
      <div
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color === "green"
          ? "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300"
          : config.color === "yellow"
            ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300"
            : "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300"
          }`}
      >
        <IconComponent className="h-3 w-3 mr-1" />
        {config.text}<br />
        {status}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const maskAccountNumber = (accountNumber: string) => {
    if (!accountNumber) return 'Not provided';
    if (accountNumber.length <= 4) return accountNumber;
    const visibleDigits = 4;
    const maskedPart = '*'.repeat(accountNumber.length - visibleDigits);
    return maskedPart + accountNumber.slice(-visibleDigits);
  };

  const maskWalletAddress = (address: string) => {
    if (!address) return 'Not provided';
    if (address.length <= 10) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <Header variant="dashboard" />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Loading your profile...</p>
          </div>
        </div>
      </div>
    );
  }


  if (!authStoreUser) {
    navigate("/")
    return null;
  }


  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <Header variant="dashboard" />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className="relative">
                {profileData.profilePictureUrl ? (
                  <img
                    src={profileData.profilePictureUrl}
                    alt="Profile"
                    className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shadow-lg">
                    <User className="h-10 w-10 text-white" />
                  </div>
                )}
              </div>
              <div>
                <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {profileData.firstName && profileData.lastName
                    ? `${profileData.firstName} ${profileData.lastName}`
                    : 'Your Profile'
                  }
                </h1>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {profileData.email}
                </p>
              </div>
            </div>


          </div>
        </div>
        <Link
          to="/settings"
          className="flex items-center space-x-1.5 md:space-x-2 bg-blue-600 text-white px-3 py-2 md:px-6 md:py-3 rounded-lg md:rounded-xl text-sm md:text-base font-semibold hover:bg-blue-700 transition-all duration-300 w-fit"
        >

          <Edit className="h-4 w-4 md:h-5 md:w-5" />
          <span>Edit Profile</span>
        </Link>

        {/* Tabs */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-3xl shadow-xl overflow-hidden`}>
          {/* Tab Navigation */}
          <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <nav className="flex space-x-8 px-8 pt-6 overflow-x-auto no-scrollbar scroll-smooth">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}

                  className={`flex items-center space-x-2 pb-4 border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                >
                  <tab.icon className="h-5 w-5 flex-shrink-0" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-8">
            {/* Tab 0: Personal Information */}
            {activeTab === 0 && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                      Personal Information
                    </h3>
                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      Your personal details and contact information
                    </p>
                  </div>
                  <Link
                    to="/settings"
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <SettingsIcon className="h-4 w-4" />
                    <span>Update Information</span>
                  </Link>
                </div>

                {/* Personal Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-4`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <User className={`h-5 w-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        First Name
                      </span>
                    </div>
                    <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {profileData.firstName || 'Not provided'}
                    </p>
                  </div>

                  <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-4`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <User className={`h-5 w-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Last Name
                      </span>
                    </div>
                    <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {profileData.lastName || 'Not provided'}
                    </p>
                  </div>

                  <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-4`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <Mail className={`h-5 w-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Email Address
                      </span>
                    </div>
                    <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {profileData.email}
                    </p>
                  </div>

                  <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-4`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <Phone className={`h-5 w-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Phone Number
                      </span>
                    </div>
                    <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {profileData.phone || 'Not provided'}
                    </p>
                  </div>

                  <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-4`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <Calendar className={`h-5 w-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Date of Birth
                      </span>
                    </div>
                    <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {formatDate(profileData.dob)}
                    </p>
                  </div>

                  <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-4 md:col-span-2`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <MapPin className={`h-5 w-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Address
                      </span>
                    </div>
                    <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {profileData.address || 'Not provided'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 1: Payment Details */}
            {activeTab === 1 && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                      Payment & Withdrawal Details
                    </h3>
                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      Your bank account and crypto wallet information
                    </p>
                  </div>
                  <Link
                    to="/settings"
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <SettingsIcon className="h-4 w-4" />
                    <span>Update Payment Details</span>
                  </Link>
                </div>

                {/* Bank Account Details */}
                <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-2xl p-6`}>
                  <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center`}>
                    <Building2 className="h-5 w-5 mr-2 text-blue-600" />
                    Bank Account Details
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                        Bank Name
                      </label>
                      <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {profileData.bankName || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                        Account Number
                      </label>
                      {/* <p className={`text-lg font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {maskAccountNumber(profileData.bankAccountNumber)}
                      </p> */}
                      <div className="flex items-center space-x-2">
                        <p className={`text-lg font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {maskAccountNumber(profileData.bankAccountNumber)}
                        </p>
                        {profileData.bankAccountNumber && profileData.bankAccountNumber !== 'Not provided' && (
                          <button
                            onClick={() => copyToClipboard(profileData.bankAccountNumber, 'accountNumber')}
                            className={`p-2 rounded-lg transition-colors ${copiedStates.accountNumber
                              ? 'bg-green-500 text-white'
                              : isDark
                                ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                              }`}
                            title={copiedStates.accountNumber ? 'Copied!' : 'Copy account number'}
                          >
                            {copiedStates.accountNumber ? (
                              <CheckCheck className="h-4 w-4" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                      {copiedStates.accountNumber && (
                        <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                          Account number copied to clipboard!
                        </p>
                      )}
                    </div>

                    {/* </div> */}

                    <div className="md:col-span-2">
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                        Account Name
                      </label>
                      <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {profileData.bankAccountName || 'Not provided'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Crypto Wallet Details */}
                <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-2xl p-6`}>
                  <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center`}>
                    <Wallet className="h-5 w-5 mr-2 text-purple-600" />
                    Crypto Wallet Details
                  </h4>

                  <h5 className={`text-md font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center`}>
                    <img
                      src={usdt}
                      alt="USDT Logo"
                      className="h-6 w-6 mr-2"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <span className="text-green-600 dark:text-green-400 mr-2">USDT</span>
                    (Binance Chain)
                  </h5>



                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                        Crypto Network
                      </label>
                      <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {profileData.network}
                      </p>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                        Wallet Address
                      </label>
                      {/* <p className={`text-lg font-mono break-all ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {maskWalletAddress(profileData.wallet_address)}
                      </p> */}
                      <div className="flex items-start space-x-2">
                        <p className={`text-lg font-mono break-all flex-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {maskWalletAddress(profileData.wallet_address)}
                        </p>
                        {profileData.wallet_address && profileData.wallet_address !== 'Not provided' && (
                          <button
                            onClick={() => copyToClipboard(profileData.wallet_address, 'walletAddress')}
                            className={`p-2 rounded-lg transition-colors flex-shrink-0 ${copiedStates.walletAddress
                              ? 'bg-green-500 text-white'
                              : isDark
                                ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                              }`}
                            title={copiedStates.walletAddress ? 'Copied!' : 'Copy wallet address'}
                          >
                            {copiedStates.walletAddress ? (
                              <CheckCheck className="h-4 w-4" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                      {copiedStates.walletAddress && (
                        <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                          Wallet address copied to clipboard!
                        </p>
                      )}


                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 2: Security & Verification */}
            {activeTab === 2 && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                      Security & Verification
                    </h3>
                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      Your account security and verification status
                    </p>
                  </div>
                  <Link
                    to="/settings"
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <SettingsIcon className="h-4 w-4" />
                    <span>Manage Security</span>
                  </Link>
                </div>

                {/* Account Security */}
                <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-2xl p-6`}>
                  <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center`}>
                    <Shield className="h-5 w-5 mr-2 text-blue-600" />
                    Account Security
                  </h4>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                      <div>
                        <h5 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Password</h5>
                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          Last updated: {formatDate(profileData.updatePasswordDate || profileData.createdAt)}
                        </p>
                      </div>
                      <Link
                        to="/settings"
                        className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                      >
                        Change Password
                      </Link>
                    </div>

                    <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                      <div>
                        <h5 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Two-Factor Authentication</h5>
                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          Not enabled
                        </p>
                      </div>
                      <span className="text-gray-400 text-sm">Coming Soon</span>
                    </div>
                  </div>
                </div>

                {/* KYC Verification */}
                <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-2xl p-6`}>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center`}>
                      <FileText className="h-5 w-5 mr-2 text-green-600" />
                      KYC Verification Status
                    </h4>
                    {getVerificationStatusBadge(profileData.kycVerificationStatus)}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                        Document Type
                      </label>
                      <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {documentTypes[profileData.kycDocumentType as keyof typeof documentTypes] || ''}
                      </p>
                    </div>

                    {profileData.kycSubmittedAt && (
                      <div>
                        <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                          Submitted On
                        </label>
                        <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatDate(profileData.kycSubmittedAt)}
                        </p>
                      </div>
                    )}

                    {profileData.kycVerifiedAt && (
                      <div>
                        <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                          Verified On
                        </label>
                        <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatDate(profileData.kycVerifiedAt)}
                        </p>
                      </div>
                    )}

                    {profileData.kycDocumentUrl && (
                      <div>
                        <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                          Uploaded Document
                        </label>
                        <a
                          href={profileData.kycDocumentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          View Document
                        </a>
                      </div>
                    )}

                    {profileData.kycVerificationStatus === 'pending' && (
                      <div className="mt-4">
                        <Link
                          to="/settings?tab=3"
                          className="inline-flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                        >
                          <FileText className="h-4 w-4" />
                          <span>Upload Documents</span>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main >
    </div >
  );
}
